import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext, useAsyncData } from '@/hooks';
import { api } from '@/api/client';
import type {
  AcademicStructureUnit,
  AcademicStructureUnitNode,
  EduCoreAcademicYear,
  EduCoreClassGroup,
  IdentifierConfig,
  IdentifierConfigs,
  IdentifierRepairResult,
  IdentifierRenumberResult,
} from '@/types';
import {
  groupNameFor,
  nextGroupCodeAfter,
  parseSuffixes,
  sequenceSeed,
  splitSequence,
} from '@/lib/classGroupCodes';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { Icon } from '@/components/icons';
import styles from './AdminAcademicStructurePage.module.css';

interface UnitForm {
  name: string;
  code: string;
}

interface ClassGroupForm {
  code: string;
  name: string;
  studyMode: '' | 'DAY' | 'EVENING';
  capacity: string;
  isActive: boolean;
}

interface QuickAddRow {
  name: string;
  code: string;
}

const EMPTY_UNIT_FORM: UnitForm = { name: '', code: '' };
const EMPTY_GROUP_FORM: ClassGroupForm = {
  code: '',
  name: '',
  studyMode: '',
  capacity: '',
  isActive: true,
};

function unitId(unit: AcademicStructureUnit): string {
  return String(unit._id);
}

function collectTeachableUnits(nodes: AcademicStructureUnitNode[]): AcademicStructureUnit[] {
  const result: AcademicStructureUnit[] = [];

  const walk = (node: AcademicStructureUnitNode) => {
    const streams = node.children.filter((child) => child.unit.unitType === 'STREAM');

    if (node.unit.unitType === 'STREAM') {
      result.push(node.unit);
    } else if (node.unit.unitType === 'LEVEL' && streams.length === 0) {
      result.push(node.unit);
    }

    for (const child of node.children) {
      walk(child);
    }
  };

  for (const root of nodes) {
    walk(root);
  }

  return result;
}

function renderUnitTree(
  nodes: AcademicStructureUnitNode[],
  teachableIds: Set<string>,
  onEdit: (unit: AcademicStructureUnit) => void,
  t: (key: string) => string,
): React.ReactNode {
  return nodes.map((node) => (
    <li key={unitId(node.unit)} className={styles.unitNode}>
      <div
        className={`${styles.unitRow} ${teachableIds.has(unitId(node.unit)) ? styles.unitRowTeachable : ''}`}
      >
        <div className={styles.unitLabel}>
          <span className={styles.unitName}>{node.unit.name}</span>
          <span className={styles.unitMeta}>
            {node.unit.unitType} · {node.unit.code}
            {!node.unit.isActive ? ` · ${t('admin.academicStructure.inactive')}` : ''}
          </span>
        </div>
        <div className={styles.unitActions}>
          <button
            type="button"
            className={styles.iconBtn}
            aria-label={t('admin.academicStructure.editUnit')}
            onClick={() => onEdit(node.unit)}
          >
            <Icon name="settings" size={16} />
          </button>
        </div>
      </div>
      {node.children.length > 0 && (
        <ul className={styles.nestedList}>
          {renderUnitTree(node.children, teachableIds, onEdit, t)}
        </ul>
      )}
    </li>
  ));
}

/** Pads a sequence the same way EduCore does: six digits, e.g. 000001. */
function padSequence(value: number): string {
  return String(value).padStart(6, '0');
}

/**
 * What the next issued number will look like under the given scheme. `{seq}`
 * renders as 000001 — the smallest number the scheme will ever mint.
 */
function previewNumber(config: IdentifierConfig, modelCode: string): string {
  const year = String(new Date().getFullYear());
  return config.format
    .replaceAll('{institutionCode}', config.institutionCode || 'INST')
    .replaceAll('{year}', year)
    .replaceAll('{modelCode}', modelCode)
    .replaceAll('{seq}', padSequence(1));
}

interface NumberingSchemeEditorProps {
  config: IdentifierConfig;
  /** Two-letter model code used for the live preview, e.g. "SE". */
  modelCode: string;
  /** Admission formats may use {modelCode}; matriculation formats may not. */
  allowModelCode: boolean;
  saving: boolean;
  onSave: (next: { institutionCode: string; format: string }) => Promise<void>;
  t: (key: string) => string;
}

function NumberingSchemeEditor({
  config,
  modelCode,
  allowModelCode,
  saving,
  onSave,
  t,
}: NumberingSchemeEditorProps) {
  const [institutionCode, setInstitutionCode] = useState(config.institutionCode);
  const [format, setFormat] = useState(config.format);
  const [error, setError] = useState<string | null>(null);

  // Re-sync when the config is reloaded after a save elsewhere.
  useEffect(() => {
    setInstitutionCode(config.institutionCode);
    setFormat(config.format);
    setError(null);
  }, [config]);

  const tokens = allowModelCode
    ? ['{institutionCode}', '{year}', '{modelCode}', '{seq}']
    : ['{institutionCode}', '{year}', '{seq}'];
  const example = previewNumber({ ...config, institutionCode, format }, modelCode);

  const submit = async () => {
    const found = format.match(/\{[^}]+\}/g) ?? [];
    if (found.length === 0 || found.some((token) => !tokens.includes(token))) {
      const forbidden = found.filter((token) => !tokens.includes(token));
      setError(
        forbidden.length > 0
          ? t('admin.academicStructure.numberingForbiddenToken') + `: ${forbidden.join(', ')}`
          : t('admin.academicStructure.numberingNoTokens'),
      );
      return;
    }
    setError(null);
    await onSave({ institutionCode, format });
  };

  return (
    <div className={styles.numberingPanel}>
      <h3 className={styles.numberingPanelTitle}>
        {t(
          config.identifierType === 'ADMISSION'
            ? 'admin.academicStructure.admissionScheme'
            : 'admin.academicStructure.matriculationScheme',
        )}
      </h3>
      <p className={styles.hintText}>
        {t(
          config.identifierType === 'ADMISSION'
            ? 'admin.academicStructure.admissionSchemeHint'
            : 'admin.academicStructure.matriculationSchemeHint',
        )}
      </p>

      <div className={styles.fieldRow}>
        <label className={styles.fieldLabel} htmlFor={`numbering-code-${config.identifierType}`}>
          {t('admin.academicStructure.institutionCode')}
        </label>
        <input
          id={`numbering-code-${config.identifierType}`}
          className={styles.fieldInput}
          value={institutionCode}
          maxLength={12}
          onChange={(e) => setInstitutionCode(e.target.value.toUpperCase())}
        />
      </div>

      <div className={styles.fieldRow}>
        <label className={styles.fieldLabel} htmlFor={`numbering-format-${config.identifierType}`}>
          {t('admin.academicStructure.format')}
        </label>
        <input
          id={`numbering-format-${config.identifierType}`}
          className={`${styles.fieldInput} ${styles.monoInput}`}
          value={format}
          onChange={(e) => setFormat(e.target.value)}
          placeholder={allowModelCode ? '{institutionCode}/{year}/{modelCode}/{seq}' : '{institutionCode}/MT/{year}/{seq}'}
        />
        <div className={styles.tokenRow}>
          {tokens.map((token) => (
            <span key={token} className={styles.tokenChip}>
              {token}
            </span>
          ))}
        </div>
        {error && <p className={styles.errorText}>{error}</p>}
      </div>

      <div className={styles.previewBox}>
        <span className={styles.previewLabel}>{t('admin.academicStructure.nextNumber')}</span>
        <span className={styles.previewValue}>{example}</span>
      </div>

      <div className={styles.numberingActions}>
        <Button onClick={submit} disabled={saving || !institutionCode.trim() || !format.trim()}>
          {saving ? t('common.submitting') : t('common.save')}
        </Button>
      </div>
    </div>
  );
}

export function AdminAcademicStructurePage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { activeContext } = useAppContext();
  const institutionId = activeContext?.institution?.id ?? '';

  const { state: unitsState, reload: reloadUnits } = useAsyncData<AcademicStructureUnitNode[]>(
    `admin-academic-units-${institutionId}`,
    () => api.admin.academicStructure.listUnits(institutionId),
    !!institutionId,
  );

  const { state: yearsState } = useAsyncData<EduCoreAcademicYear[]>(
    `admin-educore-years-${institutionId}`,
    () => api.admin.academicStructure.listYears(institutionId),
    !!institutionId,
  );

  const years = yearsState.status === 'ready' ? yearsState.data : [];
  const [selectedYearId, setSelectedYearId] = useState('');

  const effectiveYearId = useMemo(() => {
    if (selectedYearId) return selectedYearId;
    const active = years.find((y) => y.state === 'Active');
    return active?._id ?? years[0]?._id ?? '';
  }, [selectedYearId, years]);

  const { state: groupsState, reload: reloadGroups, replace: replaceGroups } = useAsyncData<
    EduCoreClassGroup[]
  >(
    `admin-class-groups-${institutionId}-${effectiveYearId}`,
    () => api.admin.academicStructure.listClassGroups(institutionId, effectiveYearId),
    !!institutionId && !!effectiveYearId,
  );

  const { state: numberingState, reload: reloadNumbering } = useAsyncData<IdentifierConfigs>(
    `admin-numbering-${institutionId}`,
    () => api.admin.academicStructure.getNumberingConfig(institutionId),
    !!institutionId,
  );
  const numbering = numberingState.status === 'ready' ? numberingState.data : undefined;
  const [savingScheme, setSavingScheme] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [repairResult, setRepairResult] = useState<IdentifierRepairResult | null>(null);
  const [renumbering, setRenumbering] = useState(false);
  const [renumberResult, setRenumberResult] = useState<IdentifierRenumberResult | null>(null);

  const [unitModal, setUnitModal] = useState<AcademicStructureUnit | null>(null);
  const [unitForm, setUnitForm] = useState<UnitForm>(EMPTY_UNIT_FORM);
  const [groupModal, setGroupModal] = useState<{
    mode: 'edit';
    unit: AcademicStructureUnit;
    group: EduCoreClassGroup;
  } | null>(null);
  const [groupForm, setGroupForm] = useState<ClassGroupForm>(EMPTY_GROUP_FORM);
  const [quickAdd, setQuickAdd] = useState<AcademicStructureUnit | null>(null);
  const [quickAddBase, setQuickAddBase] = useState('');
  const [quickAddSuffixes, setQuickAddSuffixes] = useState('');
  const [quickAddCapacity, setQuickAddCapacity] = useState('');
  const [quickAddStudyMode, setQuickAddStudyMode] = useState<'' | 'DAY' | 'EVENING'>('');
  const [quickAddRows, setQuickAddRows] = useState<QuickAddRow[]>([]);
  const [saving, setSaving] = useState(false);

  const units = unitsState.status === 'ready' ? unitsState.data : [];
  const teachableUnits = useMemo(() => collectTeachableUnits(units), [units]);
  const teachableIds = useMemo(
    () => new Set(teachableUnits.map((u) => unitId(u))),
    [teachableUnits],
  );
  const groups = groupsState.status === 'ready' ? groupsState.data : [];

  const groupsByUnit = useMemo(() => {
    const map = new Map<string, EduCoreClassGroup[]>();
    for (const group of groups) {
      const key = String(group.unitId);
      const list = map.get(key) ?? [];
      list.push(group);
      map.set(key, list);
    }
    return map;
  }, [groups]);

  const openUnitEdit = useCallback((unit: AcademicStructureUnit) => {
    setUnitModal(unit);
    setUnitForm({ name: unit.name, code: unit.code });
  }, []);

  const openGroupEdit = useCallback((unit: AcademicStructureUnit, group: EduCoreClassGroup) => {
    setGroupModal({ mode: 'edit', unit, group });
    setGroupForm({
      code: group.code,
      name: group.name,
      studyMode: group.studyMode === 'DAY' || group.studyMode === 'EVENING' ? group.studyMode : '',
      capacity: group.capacity != null ? String(group.capacity) : '',
      isActive: group.isActive,
    });
  }, []);

  /**
   * The quick-add opens with the base name set to the unit's name and the next
   * expected section pre-typed — a unit whose last group is "FORM-1-C" opens on
   * "D" — so a busy administrator often only has to press Save.
   */
  const openQuickAdd = useCallback(
    (unit: AcademicStructureUnit) => {
      const unitCodes = (groupsByUnit.get(unitId(unit)) ?? []).map((g) => g.code);
      const seed = sequenceSeed(unitCodes, unit.code, unit.name);
      const next = seed ? nextGroupCodeAfter(seed) : 'A';
      const suggested = splitSequence(next)?.run ?? next;
      setQuickAdd(unit);
      setQuickAddBase(unit.name);
      setQuickAddSuffixes(suggested);
      setQuickAddCapacity('');
      setQuickAddStudyMode('');
    },
    [groupsByUnit],
  );

  useEffect(() => {
    if (!quickAdd) {
      setQuickAddRows([]);
      return;
    }
    const unitCodes = (groupsByUnit.get(unitId(quickAdd)) ?? []).map((g) => g.code);
    const seed = sequenceSeed(unitCodes, quickAdd.code, quickAddBase);
    let cursor = seed;
    setQuickAddRows(
      parseSuffixes(quickAddSuffixes).map((suffix) => {
        const code = cursor ? nextGroupCodeAfter(cursor) : 'A';
        cursor = code;
        return { name: groupNameFor(quickAddBase, suffix), code };
      }),
    );
  }, [quickAdd, quickAddBase, quickAddSuffixes, groupsByUnit]);

  const quickAddLastCode = useMemo(() => {
    if (!quickAdd) return '';
    const unitCodes = (groupsByUnit.get(unitId(quickAdd)) ?? []).map((g) => g.code);
    return unitCodes.length > 0 ? unitCodes[unitCodes.length - 1] : '';
  }, [quickAdd, groupsByUnit]);

  const saveUnit = useCallback(async () => {
    if (!unitModal) return;
    setSaving(true);
    try {
      await api.admin.academicStructure.updateUnit(institutionId, unitId(unitModal), {
        name: unitForm.name.trim(),
        code: unitForm.code.trim(),
      });
      setUnitModal(null);
      toast.success(t('admin.academicStructure.unitSaved'));
      reloadUnits();
    } catch {
      toast.error(t('common.saveFailed'));
    } finally {
      setSaving(false);
    }
  }, [unitModal, unitForm, institutionId, toast, t, reloadUnits]);

  const saveGroup = useCallback(async () => {
    if (!groupModal) return;
    setSaving(true);
    try {
      const capacity = groupForm.capacity.trim()
        ? Number.parseInt(groupForm.capacity, 10)
        : null;

      const updated = await api.admin.academicStructure.updateClassGroup(
        institutionId,
        groupModal.group._id,
        {
          code: groupForm.code.trim(),
          name: groupForm.name.trim(),
          studyMode: groupForm.studyMode || null,
          capacity,
          isActive: groupForm.isActive,
        },
      );
      if (groupsState.status === 'ready') {
        replaceGroups(groupsState.data.map((g) => (g._id === updated._id ? updated : g)));
      } else {
        reloadGroups();
      }
      setGroupModal(null);
      toast.success(t('admin.academicStructure.groupSaved'));
    } catch {
      toast.error(t('common.saveFailed'));
    } finally {
      setSaving(false);
    }
  }, [groupModal, groupForm, institutionId, groupsState, replaceGroups, reloadGroups, toast, t]);

  const saveQuickAdd = useCallback(async () => {
    if (!quickAdd || !effectiveYearId) return;
    setSaving(true);
    try {
      const capacity = quickAddCapacity.trim()
        ? Number.parseInt(quickAddCapacity, 10)
        : null;

      const created = await api.admin.academicStructure.bulkCreateClassGroups(institutionId, {
        academicYearId: effectiveYearId,
        unitId: unitId(quickAdd),
        items: quickAddRows.map((row) => ({
          code: row.code.trim(),
          name: row.name.trim(),
          studyMode: quickAddStudyMode || null,
          capacity,
        })),
      });
      if (groupsState.status === 'ready') {
        replaceGroups([...groupsState.data, ...created]);
      } else {
        reloadGroups();
      }
      setQuickAdd(null);
      toast.success(t('admin.academicStructure.groupsSaved'));
    } catch {
      toast.error(t('common.saveFailed'));
    } finally {
      setSaving(false);
    }
  }, [
    quickAdd,
    effectiveYearId,
    quickAddCapacity,
    quickAddStudyMode,
    quickAddRows,
    institutionId,
    groupsState,
    replaceGroups,
    reloadGroups,
    toast,
    t,
  ]);

  const saveScheme = useCallback(
    async (
      identifierType: 'ADMISSION' | 'MATRICULATION',
      next: { institutionCode: string; format: string },
    ) => {
      setSavingScheme(true);
      try {
        await api.admin.academicStructure.updateNumberingConfig(institutionId, {
          identifierType,
          institutionCode: next.institutionCode.trim(),
          format: next.format.trim(),
        });
        toast.success(t('admin.academicStructure.numberingSaved'));
        reloadNumbering();
      } catch {
        toast.error(t('common.saveFailed'));
      } finally {
        setSavingScheme(false);
      }
    },
    [institutionId, toast, t, reloadNumbering],
  );

  const runRepair = useCallback(async () => {
    setRepairing(true);
    try {
      const result = await api.admin.academicStructure.repairIdentifiers(institutionId);
      setRepairResult(result);
      toast.success(t('admin.academicStructure.repairComplete'));
    } catch {
      toast.error(t('admin.academicStructure.repairFailed'));
    } finally {
      setRepairing(false);
    }
  }, [institutionId, toast, t]);

  const runRenumber = useCallback(async () => {
    setRenumbering(true);
    try {
      const result = await api.admin.academicStructure.renumberIdentifiers(institutionId);
      setRenumberResult(result);
      toast.success(t('admin.academicStructure.renumberComplete'));
    } catch {
      toast.error(t('admin.academicStructure.renumberFailed'));
    } finally {
      setRenumbering(false);
    }
  }, [institutionId, toast, t]);

  if (unitsState.status === 'loading' || yearsState.status === 'loading') {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>{t('common.loading')}</div>
      </div>
    );
  }

  if (unitsState.status === 'error') {
    return (
      <div className={styles.page}>
        <Card className={styles.emptyState}>
          <Icon name={unitsState.error.isMissingEndpoint ? 'info' : 'error'} size={48} />
          <p className={styles.emptyStateBody}>
            {t(
              unitsState.error.isMissingEndpoint
                ? 'admin.academicStructure.notAvailable'
                : 'platform.failure.generic.body',
            )}
          </p>
          <button type="button" className={styles.retryButton} onClick={reloadUnits}>
            {t('common.retry')}
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className="page-header page-headerRow">
        <div>
          <h1>{t('admin.academicStructure.title')}</h1>
          <p className="page-subtitle">{t('admin.academicStructure.subtitle')}</p>
        </div>
      </div>

      <div className={styles.toolbar}>
        <label htmlFor="academic-year-select">
          <span className="sr-only">{t('admin.academicStructure.academicYear')}</span>
          <select
            id="academic-year-select"
            className={styles.yearSelect}
            value={effectiveYearId}
            onChange={(e) => setSelectedYearId(e.target.value)}
          >
            {years.length === 0 ? (
              <option value="">{t('admin.academicStructure.noYears')}</option>
            ) : (
              years.map((year) => (
                <option key={year._id} value={year._id}>
                  {year.label} ({year.state})
                </option>
              ))
            )}
          </select>
        </label>
      </div>

      <Card className={`${styles.panel} ${styles.numberingSection}`}>
        <h2 className={styles.panelTitle}>{t('admin.academicStructure.numberingTitle')}</h2>
        <p className={styles.hintText}>{t('admin.academicStructure.numberingSubtitle')}</p>
        {numberingState.status === 'loading' ? (
          <div className={styles.loading}>{t('common.loading')}</div>
        ) : numberingState.status === 'error' ? (
          <p className={styles.emptyStateBody}>
            {t(
              numberingState.error.isMissingEndpoint
                ? 'admin.academicStructure.notAvailable'
                : 'platform.failure.generic.body',
            )}
          </p>
        ) : numbering ? (
          <>
            <div className={styles.numberingGrid}>
              <NumberingSchemeEditor
                config={numbering.admission}
                modelCode="SE"
                allowModelCode
                saving={savingScheme}
                onSave={(next) => saveScheme('ADMISSION', next)}
                t={t}
              />
              <NumberingSchemeEditor
                config={numbering.matriculation}
                modelCode="SE"
                allowModelCode={false}
                saving={savingScheme}
                onSave={(next) => saveScheme('MATRICULATION', next)}
                t={t}
              />
            </div>
            <div className={styles.repairRow}>
              <Button variant="secondary" onClick={runRepair} disabled={repairing}>
                {repairing ? t('common.submitting') : t('admin.academicStructure.runBackfill')}
              </Button>
              {repairResult && (
                <span className={styles.repairResult}>
                  {t('admin.academicStructure.repairResult', {
                    checked: repairResult.checked,
                    repaired: repairResult.repaired,
                    complete: repairResult.alreadyComplete,
                  })}
                </span>
              )}
            </div>
            <div className={styles.repairRow}>
              <Button variant="secondary" onClick={runRenumber} disabled={renumbering}>
                {renumbering
                  ? t('common.submitting')
                  : t('admin.academicStructure.runRenumber')}
              </Button>
              {renumberResult && (
                <span className={styles.repairResult}>
                  {t('admin.academicStructure.renumberResult', {
                    renumbered: renumberResult.renumbered,
                    unchanged: renumberResult.unchanged,
                    skipped: renumberResult.skipped,
                  })}
                </span>
              )}
            </div>
          </>
        ) : null}
      </Card>

      <div className={styles.layout}>
        <Card className={styles.panel}>
          <h2 className={styles.panelTitle}>{t('admin.academicStructure.levelsTitle')}</h2>
          {units.length === 0 ? (
            <p className={styles.emptyStateBody}>{t('admin.academicStructure.noLevels')}</p>
          ) : (
            <ul className={styles.unitTree}>
              {renderUnitTree(units, teachableIds, openUnitEdit, t)}
            </ul>
          )}
        </Card>

        <div>
          {teachableUnits.length === 0 ? (
            <Card className={styles.emptyState}>
              <p className={styles.emptyStateBody}>{t('admin.academicStructure.noTeachable')}</p>
            </Card>
          ) : (
            teachableUnits.map((unit) => {
              const unitGroups = groupsByUnit.get(unitId(unit)) ?? [];
              return (
                <section key={unitId(unit)} className={styles.groupSection}>
                  <Card className={styles.panel}>
                    <div className={styles.groupSectionHeader}>
                      <h3 className={styles.groupSectionTitle}>{unit.name}</h3>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={!effectiveYearId}
                        onClick={() => openQuickAdd(unit)}
                      >
                        {t('admin.academicStructure.addGroup')}
                      </Button>
                    </div>
                    {unitGroups.length === 0 ? (
                      <p className={styles.emptyStateBody}>
                        {t('admin.academicStructure.noGroupsForUnit')}
                      </p>
                    ) : (
                      <div className="table-scroll">
                        <table className={styles.groupTable}>
                        <thead>
                          <tr>
                            <th>{t('admin.academicStructure.groupCode')}</th>
                            <th>{t('admin.academicStructure.groupName')}</th>
                            <th>{t('admin.academicStructure.studyMode')}</th>
                            <th>{t('admin.academicStructure.enrolled')}</th>
                            <th>{t('admin.academicStructure.status')}</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {unitGroups.map((group) => (
                            <tr key={group._id}>
                              <td>{group.code}</td>
                              <td>{group.name}</td>
                              <td>
                                {group.studyMode === 'DAY'
                                  ? t('admin.academicStructure.studyModeDay')
                                  : group.studyMode === 'EVENING'
                                    ? t('admin.academicStructure.studyModeEvening')
                                    : '—'}
                              </td>
                              <td>{group.headcount ?? 0}</td>
                              <td>
                                {!group.isActive && (
                                  <span className={styles.inactiveBadge}>
                                    {t('admin.academicStructure.inactive')}
                                  </span>
                                )}
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className={styles.iconBtn}
                                  aria-label={t('admin.academicStructure.editGroup')}
                                  onClick={() => openGroupEdit(unit, group)}
                                >
                                  <Icon name="settings" size={16} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        </table>
                      </div>
                    )}
                  </Card>
                </section>
              );
            })
          )}
        </div>
      </div>

      <Modal
        open={!!unitModal}
        onClose={() => setUnitModal(null)}
        title={t('admin.academicStructure.editUnitTitle')}
      >
        <div className={styles.fieldRow}>
          <label className={styles.fieldLabel} htmlFor="unit-name">
            {t('admin.academicStructure.unitName')}
          </label>
          <input
            id="unit-name"
            className={styles.fieldInput}
            value={unitForm.name}
            onChange={(e) => setUnitForm((f) => ({ ...f, name: e.target.value }))}
          />
        </div>
        <div className={styles.fieldRow}>
          <label className={styles.fieldLabel} htmlFor="unit-code">
            {t('admin.academicStructure.unitCode')}
          </label>
          <input
            id="unit-code"
            className={styles.fieldInput}
            value={unitForm.code}
            onChange={(e) => setUnitForm((f) => ({ ...f, code: e.target.value }))}
          />
        </div>
        <div className={styles.modalActions}>
          <Button variant="secondary" onClick={() => setUnitModal(null)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={saveUnit} disabled={saving || !unitForm.name.trim()}>
            {t('common.save')}
          </Button>
        </div>
      </Modal>

      <Modal
        open={!!quickAdd}
        onClose={() => setQuickAdd(null)}
        title={t('admin.academicStructure.addGroupTitle')}
      >
        <div className={styles.fieldRow}>
          <label className={styles.fieldLabel} htmlFor="quick-add-base">
            {t('admin.academicStructure.quickAddBaseName')}
          </label>
          <input
            id="quick-add-base"
            className={styles.fieldInput}
            value={quickAddBase}
            onChange={(e) => setQuickAddBase(e.target.value)}
            placeholder={t('admin.academicStructure.quickAddBaseNamePlaceholder')}
          />
        </div>
        <div className={styles.fieldRow}>
          <label className={styles.fieldLabel} htmlFor="quick-add-suffixes">
            {t('admin.academicStructure.quickAddSuffixes')}
          </label>
          <input
            id="quick-add-suffixes"
            className={styles.fieldInput}
            value={quickAddSuffixes}
            onChange={(e) => setQuickAddSuffixes(e.target.value)}
            placeholder={t('admin.academicStructure.quickAddSuffixesPlaceholder')}
          />
          <p className={styles.hintText}>
            {t('admin.academicStructure.quickAddSuffixesHint')}
          </p>
        </div>
        <div className={styles.fieldRow}>
          <label className={styles.fieldLabel} htmlFor="quick-add-capacity">
            {t('admin.academicStructure.capacityAll')}
          </label>
          <input
            id="quick-add-capacity"
            type="number"
            min={1}
            className={styles.fieldInput}
            value={quickAddCapacity}
            onChange={(e) => setQuickAddCapacity(e.target.value)}
            placeholder={t('admin.academicStructure.capacityPlaceholder')}
          />
        </div>
        <div className={styles.fieldRow}>
          <label className={styles.fieldLabel} htmlFor="quick-add-study-mode">
            {t('admin.academicStructure.studyMode')}
          </label>
          <select
            id="quick-add-study-mode"
            className={styles.fieldInput}
            value={quickAddStudyMode}
            onChange={(e) =>
              setQuickAddStudyMode(e.target.value as '' | 'DAY' | 'EVENING')
            }
          >
            <option value="">{t('admin.academicStructure.studyModeUnset')}</option>
            <option value="DAY">{t('admin.academicStructure.studyModeDay')}</option>
            <option value="EVENING">{t('admin.academicStructure.studyModeEvening')}</option>
          </select>
        </div>

        <div className={styles.fieldRow}>
          <span className={styles.fieldLabel}>{t('admin.academicStructure.quickAddPreview')}</span>
          {quickAddRows.length > 0 ? (
            <>
              <p className={styles.previewMeta}>
                {quickAddLastCode
                  ? t('admin.academicStructure.quickAddContinuingAfter', {
                      code: quickAddLastCode,
                    })
                  : t('admin.academicStructure.quickAddAutoCodes')}
              </p>
              <div className={styles.previewList}>
                {quickAddRows.map((row, index) => (
                  <div key={index} className={styles.previewRow}>
                    <span className={styles.previewName}>{row.name}</span>
                    <input
                      className={styles.previewCodeInput}
                      value={row.code}
                      aria-label={t('admin.academicStructure.groupCode')}
                      onChange={(e) =>
                        setQuickAddRows((rows) =>
                          rows.map((r, i) => (i === index ? { ...r, code: e.target.value } : r)),
                        )
                      }
                    />
                    <button
                      type="button"
                      className={styles.removeBtn}
                      aria-label={t('admin.academicStructure.removeGroup')}
                      onClick={() => setQuickAddRows((rows) => rows.filter((_, i) => i !== index))}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className={styles.hintText}>{t('admin.academicStructure.quickAddAutoCodes')}</p>
          )}
        </div>

        <div className={styles.modalActions}>
          <Button variant="secondary" onClick={() => setQuickAdd(null)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={saveQuickAdd}
            disabled={
              saving ||
              quickAddRows.length === 0 ||
              quickAddRows.some((row) => !row.code.trim() || !row.name.trim())
            }
          >
            {t('common.save')}
          </Button>
        </div>
      </Modal>

      <Modal
        open={!!groupModal}
        onClose={() => setGroupModal(null)}
        title={t('admin.academicStructure.editGroupTitle')}
      >
        <div className={styles.fieldRow}>
          <label className={styles.fieldLabel} htmlFor="group-code">
            {t('admin.academicStructure.groupCode')}
          </label>
          <input
            id="group-code"
            className={styles.fieldInput}
            value={groupForm.code}
            onChange={(e) => setGroupForm((f) => ({ ...f, code: e.target.value }))}
          />
        </div>
        <div className={styles.fieldRow}>
          <label className={styles.fieldLabel} htmlFor="group-name">
            {t('admin.academicStructure.groupName')}
          </label>
          <input
            id="group-name"
            className={styles.fieldInput}
            value={groupForm.name}
            onChange={(e) => setGroupForm((f) => ({ ...f, name: e.target.value }))}
          />
        </div>
        <div className={styles.fieldRow}>
          <label className={styles.fieldLabel} htmlFor="group-study-mode">
            {t('admin.academicStructure.studyMode')}
          </label>
          <select
            id="group-study-mode"
            className={styles.fieldInput}
            value={groupForm.studyMode}
            onChange={(e) =>
              setGroupForm((f) => ({
                ...f,
                studyMode: e.target.value as '' | 'DAY' | 'EVENING',
              }))
            }
          >
            <option value="">{t('admin.academicStructure.studyModeUnset')}</option>
            <option value="DAY">{t('admin.academicStructure.studyModeDay')}</option>
            <option value="EVENING">{t('admin.academicStructure.studyModeEvening')}</option>
          </select>
        </div>
        <div className={styles.fieldRow}>
          <label className={styles.fieldLabel} htmlFor="group-capacity">
            {t('admin.academicStructure.capacity')}
          </label>
          <input
            id="group-capacity"
            type="number"
            min={1}
            className={styles.fieldInput}
            value={groupForm.capacity}
            onChange={(e) => setGroupForm((f) => ({ ...f, capacity: e.target.value }))}
            placeholder={t('admin.academicStructure.capacityPlaceholder')}
          />
        </div>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={groupForm.isActive}
            onChange={(e) => setGroupForm((f) => ({ ...f, isActive: e.target.checked }))}
          />
          {t('admin.academicStructure.activeGroup')}
        </label>
        <div className={styles.modalActions}>
          <Button variant="secondary" onClick={() => setGroupModal(null)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={saveGroup}
            disabled={saving || !groupForm.code.trim() || !groupForm.name.trim()}
          >
            {t('common.save')}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
